/**
 * reportsPermissions.ts — Issue #397.
 *
 * Permission constants + helpers for the sales-funnel / channel-ROI summary
 * read-model. Mirrors backend @RequirePermission('reports.*', '...')
 * decorators. No React imports; pure TypeScript for testability.
 */

import { hasPermission } from './crmPermissions';

export { hasPermission };

export const REPORTS_PERMISSIONS = {
  // Issue #397 — Sales funnel & channel ROI summary read-model.
  SALES_FUNNEL_CHANNEL_ROI_SUMMARY_READ:
    'reports.sales_funnel_channel_roi_summary:read',
  SALES_FUNNEL_CHANNEL_ROI_SUMMARY_GENERATE:
    'reports.sales_funnel_channel_roi_summary:generate',
  SALES_FUNNEL_CHANNEL_ROI_SUMMARY_REVIEW:
    'reports.sales_funnel_channel_roi_summary:review',
  SALES_FUNNEL_CHANNEL_ROI_SUMMARY_PUBLISH:
    'reports.sales_funnel_channel_roi_summary:publish',
  SALES_FUNNEL_CHANNEL_ROI_SUMMARY_EXPORT:
    'reports.sales_funnel_channel_roi_summary:export',
  SALES_FUNNEL_CHANNEL_ROI_SUMMARY_REVEAL:
    'reports.sales_funnel_channel_roi_summary:reveal',
  SALES_FUNNEL_CHANNEL_ROI_SUMMARY_READ_FULL:
    'reports.sales_funnel_channel_roi_summary:read_full',
  SALES_FUNNEL_CHANNEL_ROI_SUMMARY_MANAGE:
    'reports.sales_funnel_channel_roi_summary:manage',
} as const;

export type ReportsPermission =
  (typeof REPORTS_PERMISSIONS)[keyof typeof REPORTS_PERMISSIONS];

export function canSeeSalesFunnelChannelRoiSummary(
  granted: readonly string[],
): boolean {
  return hasPermission(
    granted,
    REPORTS_PERMISSIONS.SALES_FUNNEL_CHANNEL_ROI_SUMMARY_READ,
  );
}

export function canGenerateSalesFunnelChannelRoiSummary(
  granted: readonly string[],
): boolean {
  return hasPermission(
    granted,
    REPORTS_PERMISSIONS.SALES_FUNNEL_CHANNEL_ROI_SUMMARY_GENERATE,
  );
}

export function canReviewSalesFunnelChannelRoiSummary(
  granted: readonly string[],
): boolean {
  return hasPermission(
    granted,
    REPORTS_PERMISSIONS.SALES_FUNNEL_CHANNEL_ROI_SUMMARY_REVIEW,
  );
}

export function canPublishSalesFunnelChannelRoiSummary(
  granted: readonly string[],
): boolean {
  return hasPermission(
    granted,
    REPORTS_PERMISSIONS.SALES_FUNNEL_CHANNEL_ROI_SUMMARY_PUBLISH,
  );
}

export function canExportSalesFunnelChannelRoiSummary(
  granted: readonly string[],
): boolean {
  return hasPermission(
    granted,
    REPORTS_PERMISSIONS.SALES_FUNNEL_CHANNEL_ROI_SUMMARY_EXPORT,
  );
}

export function canRevealSalesFunnelChannelRoiSummary(
  granted: readonly string[],
): boolean {
  return hasPermission(
    granted,
    REPORTS_PERMISSIONS.SALES_FUNNEL_CHANNEL_ROI_SUMMARY_REVEAL,
  );
}

export function hasFullScopeSalesFunnelChannelRoiSummary(
  granted: readonly string[],
): boolean {
  return hasPermission(
    granted,
    REPORTS_PERMISSIONS.SALES_FUNNEL_CHANNEL_ROI_SUMMARY_READ_FULL,
  );
}
